import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import { Node } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { TableKit } from '@tiptap/extension-table';
import { BoldIcon, ItalicIcon, ListIcon, ListOrderedIcon } from 'lucide-react';
import {
  BackLink,
  Button,
  ErrorNotice,
  LoadingState,
  PageHeading,
  Pill,
  SegmentedControl,
} from '@bcecloud/portal-ui';
import { cn } from '@/lib/utils';
import { errorMessage } from '@/lib/api';
import { contractPaths, subjectFromParams } from '@/lib/contracts';
import { readContract, readContractDocument, readContractLibrary, refreshContractData, updateContractDocument } from '@/lib/contracts';

/**
 * An explicit page break in the signed PDF. A custom node so tiptap keeps
 * it through editing; the canonical stylesheet turns `[data-page-break]`
 * into `page-break-after: always`.
 */
const PageBreak = Node.create({
  name: 'pageBreak',
  group: 'block',
  atom: true,
  selectable: true,
  parseHTML: () => [{ tag: 'div[data-page-break]' }],
  renderHTML: () => ['div', { 'data-page-break': 'true' }],
});

/**
 * Scopes the canonical document stylesheet to the editor surface: the
 * `body` rule becomes the surface itself and every other rule nests under
 * it. One stylesheet, served by the API — the editor cannot drift from
 * the rendered document.
 */
function scopeDocumentCss(css: string): string {
  return css
    .split('\n')
    .map((line) => {
      const match = /^(\s*)([^@\s/][^{]*)\{(.*)$/.exec(line);
      if (!match) return line; // comments, @page, closers, declarations
      const [, indent, selector, rest] = match;
      const scoped = (selector ?? '')
        .split(',')
        .map((one) => {
          const sel = one.trim();
          return sel === 'body' ? '.contract-doc-surface' : `.contract-doc-surface ${sel}`;
        })
        .join(', ');
      return `${indent}${scoped} {${rest}`;
    })
    .join('\n');
}

/** Styles a chip DOM node so signing/merge fields read as tokens in-editor. */
function styleChip(el: HTMLElement, tone: 'field' | 'merge'): void {
  el.setAttribute('contenteditable', 'false');
  el.style.cssText = [
    'display: inline-block',
    'padding: 0 6px',
    'margin: 0 1px',
    'border-radius: 4px',
    'font-family: -apple-system, sans-serif',
    'font-size: 10px',
    'font-weight: 600',
    'line-height: 18px',
    'vertical-align: baseline',
    'white-space: nowrap',
    'cursor: default',
    tone === 'field'
      ? 'color: #3b5ba5; background: #eaf0fb; border: 1px dashed #7c93b5'
      : 'color: #7a5a12; background: #fbf3e0; border: 1px solid #e4cd94',
  ].join(';');
}

/**
 * DocuSeal's field elements (per their HTML API): real fillable fields
 * placed inline in the document, sized by their inline style. Each is a
 * tiptap node that renders in the editor as a visible chip (via a NodeView)
 * and serializes back to the real `<…-field>` element for DocuSeal.
 */
const FIELD_TAGS = [
  'signature-field',
  'initials-field',
  'date-field',
  'text-field',
  'checkbox-field',
  'phone-field',
] as const;

/** One tiptap node per DocuSeal field element, preserving its attributes. */
function fieldElementNode(tag: string) {
  return Node.create({
    name: tag.replace(/-(.)/g, (_, c: string) => c.toUpperCase()),
    group: 'inline',
    inline: true,
    atom: true,
    selectable: true,
    addAttributes: () => ({
      name: {
        default: 'Field',
        parseHTML: (el: HTMLElement) => el.getAttribute('name') ?? 'Field',
        renderHTML: (attrs: Record<string, unknown>) => ({ name: attrs.name }),
      },
      role: {
        default: 'Client',
        parseHTML: (el: HTMLElement) => el.getAttribute('role') ?? 'Client',
        renderHTML: (attrs: Record<string, unknown>) => ({ role: attrs.role }),
      },
      style: {
        default: 'width: 160px; height: 18px; display: inline-block;',
        parseHTML: (el: HTMLElement) => el.getAttribute('style'),
        renderHTML: (attrs: Record<string, unknown>) =>
          attrs.style ? { style: attrs.style } : {},
      },
    }),
    parseHTML: () => [{ tag }],
    renderHTML: ({ HTMLAttributes }) => [tag, HTMLAttributes],
    addNodeView() {
      return ({ node }) => {
        const dom = document.createElement('span');
        styleChip(dom, 'field');
        const label = tag.replace('-field', '');
        dom.textContent = `✎ ${(node.attrs.name as string) || label}`;
        return { dom };
      };
    },
  });
}

const FIELD_NODES = FIELD_TAGS.map(fieldElementNode);

/** Merge keys → human labels (mirrors the API's MERGE_FIELDS). */
const MERGE_LABELS: Record<string, string> = {
  contract_number: 'Contract number',
  program_name: 'Program name',
  event_date: 'Event date',
  event_time: 'Event time',
  venue_name: 'Venue',
  venue_address: 'Venue address',
  participants: 'Participants',
  contracted_amount: 'Contracted amount',
  paid_to_date: 'Paid to date',
  balance_due: 'Balance due',
  payment_terms: 'Payment terms',
  client_name: 'Client',
  organization_name: 'Provider',
  contract_date: 'Contract date',
  price_valid_until: 'Price valid until',
  salesperson_name: 'Salesperson',
  salesperson_title: 'Salesperson title',
  salesperson_extension: 'Salesperson extension',
  salesperson_email: 'Salesperson email',
  salesperson_phone: 'Salesperson phone',
};

/** A series contract's per-event keys (mirrors the API's EVENT_MERGE_FIELDS). */
const EVENT_MERGE_LABELS: Record<string, string> = {
  program_name: 'program',
  event_date: 'date',
  event_time: 'time',
  venue_name: 'venue',
  venue_address: 'venue address',
  participants: 'participants',
  event_total: 'total',
};

/** A merge key's label: "Venue", or "Event 2 venue" for a series event's field. */
function mergeLabel(key: string): string {
  const perEvent = /^event_(\d+)_([a-z_]+)$/.exec(key);
  const eventField = perEvent?.[2] ? EVENT_MERGE_LABELS[perEvent[2]] : undefined;
  if (perEvent && eventField) return `Event ${perEvent[1]} ${eventField}`;
  return MERGE_LABELS[key] ?? key;
}

/**
 * A program-data merge field: `<span data-merge="key">value</span>`. Shows
 * the current injected value but stays tagged, so regenerating with fresh
 * event data updates it while leaving manual edits alone. When empty it
 * shows its field label so you can see what will fill in.
 */
const MergeField = Node.create({
  name: 'mergeField',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  addAttributes: () => ({
    key: {
      default: '',
      parseHTML: (el: HTMLElement) => el.getAttribute('data-merge') ?? '',
      renderHTML: (attrs: Record<string, unknown>) => ({ 'data-merge': attrs.key }),
    },
    value: {
      default: '',
      parseHTML: (el: HTMLElement) => el.textContent ?? '',
      renderHTML: () => ({}),
    },
  }),
  parseHTML: () => [{ tag: 'span[data-merge]' }],
  renderHTML: ({ node, HTMLAttributes }) => ['span', HTMLAttributes, node.attrs.value as string],
  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('span');
      const key = node.attrs.key as string;
      const value = (node.attrs.value as string) ?? '';
      styleChip(dom, 'merge');
      if (value.length) {
        dom.textContent = value;
      } else {
        dom.textContent = `⌁ ${mergeLabel(key)}`;
        dom.style.opacity = '0.7';
      }
      dom.title = `Auto-filled from event data: ${mergeLabel(key)}`;
      return { dom };
    };
  },
});

/** Insert-data menu: every merge field, dropped as an empty tagged span. */
const MERGE_INSERTS = Object.entries(MERGE_LABELS).map(([key, label]) => ({
  key,
  label,
  html: `<span data-merge="${key}"></span>`,
}));

/** Insert-field menu: label + the element HTML dropped at the cursor. */
const SIGNING_FIELDS = [
  { label: 'Signature', tag: '<signature-field name="Signature" role="Client" style="width: 260px; height: 60px; display: inline-block;"></signature-field>' },
  { label: 'Initials', tag: '<initials-field name="Initials" role="Client" style="width: 100px; height: 40px; display: inline-block;"></initials-field>' },
  { label: 'Date signed', tag: '<date-field name="Date Signed" role="Client" style="width: 160px; height: 18px; display: inline-block;"></date-field>' },
  { label: 'Text input', tag: '<text-field name="Text Field" role="Client" style="width: 200px; height: 18px; display: inline-block;"></text-field>' },
  { label: 'Checkbox', tag: '<checkbox-field name="Checkbox" role="Client" style="width: 16px; height: 16px; display: inline-block;"></checkbox-field>' },
  { label: 'Phone', tag: '<phone-field name="Phone" role="Client" style="width: 180px; height: 18px; display: inline-block;"></phone-field>' },
] as const;

/** Editing surfaces: rich text for humans, raw HTML for exact control. */
const MODES = [
  { value: 'rich', label: 'Rich text' },
  { value: 'html', label: 'HTML' },
] as const;

type Mode = (typeof MODES)[number]['value'];

/** Splits a full document into its head (styles) and body content. */
function splitDocument(full: string): { head: string; body: string } {
  const parsed = new DOMParser().parseFromString(full, 'text/html');
  return { head: parsed.head.innerHTML, body: parsed.body.innerHTML };
}

/** Reassembles the full document DocuSeal receives. */
function joinDocument(head: string, body: string): string {
  return `<html>\n<head>\n${head}\n</head>\n<body>\n${body}\n</body>\n</html>`;
}

/**
 * Contract text editor. Rich mode edits the document body with tiptap
 * (tables editable in place); the HTML tab exposes the full source for
 * exact control. The document's <head> styles ride along untouched either
 * way, and saving regenerates the provider template — this is where the
 * actual contract wording changes; the provider's builder only places
 * fields on top.
 *
 * @returns The text editor page.
 */
export function ContractTextPage(): React.ReactElement {
  const { eventId, campaignId, requestId, organizationId, contractId } = useParams<{ eventId?: string; campaignId?: string; requestId?: string; organizationId?: string; contractId: string }>();
  const paths = contractPaths(subjectFromParams({ eventId, campaignId, requestId, organizationId }));
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>('rich');
  const [head, setHead] = useState<string | null>(null);
  const [body, setBody] = useState<string>('');
  const [htmlDraft, setHtmlDraft] = useState<string>('');
  /** Bumped when the HTML tab rewrites the doc, to remount the rich editor. */
  const [richVersion, setRichVersion] = useState(0);

  const contract = useQuery({
    queryKey: ['contract', contractId],
    queryFn: () => readContract(contractId ?? ''),
    enabled: contractId !== undefined,
  });

  const document = useQuery({
    queryKey: ['contract-document', contractId],
    queryFn: () => readContractDocument(contractId ?? ''),
    enabled: contractId !== undefined,
    staleTime: 0,
  });

  // Seed the editor once the source arrives; edits are local after that.
  useEffect(() => {
    if (document.data !== undefined && head === null) {
      const parts = splitDocument(document.data.html);
      setHead(parts.head);
      setBody(parts.body);
    }
  }, [document.data, head]);

  const fullHtml = useMemo(
    () => (head === null ? '' : joinDocument(head, body)),
    [head, body],
  );

  const save = useMutation({
    mutationFn: () => updateContractDocument(contractId ?? '', fullHtml),
    onSuccess: () => navigate(paths.back),
  });

  const refreshData = useMutation({
    mutationFn: () => refreshContractData(contractId ?? ''),
    // The refilled document is re-fetched from the server, not returned here.
    onSuccess: () => window.location.reload(),
  });

  /** Entering/leaving the HTML tab keeps both representations in sync. */
  function switchMode(next: Mode): void {
    if (next === mode) return;
    if (next === 'html') {
      setHtmlDraft(fullHtml);
    } else {
      const parts = splitDocument(htmlDraft);
      setHead(parts.head);
      setBody(parts.body);
      setRichVersion((v) => v + 1);
    }
    setMode(next);
  }

  const previewHtml = mode === 'html' ? htmlDraft : fullHtml;

  return (
    <section className="animate-portal-rise">
      <BackLink to={paths.back}>Back to contracts</BackLink>

      <div className="mt-3">
        <PageHeading
          eyebrow="EDIT CONTRACT TEXT"
          size="compact"
          title={contract.data?.name ?? 'Contract'}
          action={
            <div className="flex items-center gap-2">
              {contract.data ? <Pill tone="neutral">Revision {contract.data.revision}</Pill> : null}
              <Button
                size="compact"
                variant="secondary"
                onClick={() => refreshData.mutate()}
                disabled={refreshData.isPending}
                title="Re-fill the tagged program fields from the event's current data"
              >
                {refreshData.isPending ? 'Refreshing…' : campaignId ? 'Refresh campaign data' : 'Refresh event data'}
              </Button>
              <Button size="compact" onClick={() => save.mutate()} disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save & regenerate'}
              </Button>
            </div>
          }
        />
      </div>

      {document.isPending ? <LoadingState label="Loading document…" /> : null}
      {document.isError ? (
        <ErrorNotice title="Document unavailable" message={errorMessage(document.error)} />
      ) : null}
      {save.isError ? (
        <div className="mt-3">
          <ErrorNotice inline title="Save failed" message={errorMessage(save.error)} />
        </div>
      ) : null}

      {head !== null ? (
        <>
          <div className="mt-4 flex items-center justify-between gap-3">
            <SegmentedControl<Mode>
              label="Editing surface"
              options={MODES.map((m) => ({ key: m.value, label: m.label }))}
              value={mode}
              onChange={switchMode}
            />
            <p className="text-portal-micro text-portal-muted">
              The dashed boxes are live signing fields — move or add them via "Insert field".
            </p>
          </div>

          {mode === 'rich' ? (
            /* The editor IS the preview: its surface mirrors the document
               stylesheet 1:1, and saving re-wraps the body in that same
               stylesheet server-side — the two cannot drift. */
            <div className="mt-3">
              <ContractDocEditor key={richVersion} initialHtml={body} onChange={setBody} />
            </div>
          ) : (
            <div className="mt-3 grid gap-4 lg:grid-cols-2">
              <textarea
                aria-label="Document HTML"
                value={htmlDraft}
                onChange={(e) => setHtmlDraft(e.target.value)}
                spellCheck={false}
                className="h-[70vh] w-full resize-none rounded-portal-button border bg-white p-3 font-mono text-xs leading-5 text-portal-ink focus-visible:outline-none focus-visible:ring-2"
              />
              <div>
                <p className="mb-1 text-portal-small font-bold text-portal-ink">Preview</p>
                <iframe
                  title="Contract preview"
                  srcDoc={previewHtml}
                  sandbox=""
                  className="h-[68vh] w-full rounded-portal-button border bg-white"
                />
              </div>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}

/**
 * The contract-body rich editor: StarterKit plus in-place table editing.
 * Emits body HTML on every change; the caller owns wrapping it back into
 * the full document.
 */
function ContractDocEditor({
  initialHtml,
  onChange,
}: {
  readonly initialHtml: string;
  readonly onChange: (html: string) => void;
}): React.ReactElement {
  // The canonical stylesheet + brand row, straight from the API — the
  // editor surface renders with the document's own styles.
  const library = useQuery({
    queryKey: ['contract-library'],
    queryFn: ({ signal }) => readContractLibrary(signal),
    staleTime: 5 * 60 * 1000,
  });
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      TableKit,
      PageBreak,
      MergeField,
      ...FIELD_NODES,
    ],
    content: initialHtml,
    editorProps: {
      attributes: {
        'aria-label': 'Contract document',
        // Approximate the printed document so editing feels WYSIWYG: bold
        // first columns, bordered cells, uppercase section headings.
        // Mirrors CONTRACT_STYLESHEET in apps/api lib/contracts/contract-html.ts
        // — keep the two in lockstep so the editor is a truthful preview.
        class: cn(
          // Layout only — ALL typography comes from the canonical document
          // stylesheet, injected scoped by ContractDocEditor below.
          'min-h-[60vh] focus:outline-none',
                ),
      },
    },
    onUpdate: ({ editor: current }) => onChange(current.getHTML()),
  });

  if (!editor) return <div className="min-h-[64vh] rounded-portal-button border bg-white" />;

  const toolbarButton = (
    label: string,
    icon: React.ReactElement,
    active: boolean,
    run: () => void,
  ) => (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      onClick={run}
      className={cn(
        'rounded p-1.5 text-portal-ink hover:bg-portal-bg',
        active && 'bg-portal-bg font-bold',
      )}
    >
      {icon}
    </button>
  );

  return (
    <div className="rounded-portal-button border bg-white">
      <div className="flex items-center gap-1 border-b px-2 py-1">
        {toolbarButton('Bold', <BoldIcon className="size-4" />, editor.isActive('bold'), () =>
          editor.chain().focus().toggleBold().run(),
        )}
        {toolbarButton('Italic', <ItalicIcon className="size-4" />, editor.isActive('italic'), () =>
          editor.chain().focus().toggleItalic().run(),
        )}
        {toolbarButton(
          'Bullet list',
          <ListIcon className="size-4" />,
          editor.isActive('bulletList'),
          () => editor.chain().focus().toggleBulletList().run(),
        )}
        {toolbarButton(
          'Numbered list',
          <ListOrderedIcon className="size-4" />,
          editor.isActive('orderedList'),
          () => editor.chain().focus().toggleOrderedList().run(),
        )}
        <span className="mx-1 h-4 w-px bg-portal-line" aria-hidden />
        <button
          type="button"
          onClick={() =>
            editor.chain().focus().insertTable({ rows: 3, cols: 2, withHeaderRow: false }).run()
          }
          className="rounded px-2 py-1 text-portal-micro text-portal-ink hover:bg-portal-bg"
        >
          Insert table
        </button>
        {(
          [
            ['+ Row', () => editor.chain().focus().addRowAfter().run(), editor.can().addRowAfter()],
            ['− Row', () => editor.chain().focus().deleteRow().run(), editor.can().deleteRow()],
            ['+ Col', () => editor.chain().focus().addColumnAfter().run(), editor.can().addColumnAfter()],
            ['− Col', () => editor.chain().focus().deleteColumn().run(), editor.can().deleteColumn()],
          ] as const
        ).map(([label, run, enabled]) => (
          <button
            key={label}
            type="button"
            onClick={run}
            disabled={!enabled}
            className="rounded px-2 py-1 text-portal-micro text-portal-ink hover:bg-portal-bg disabled:opacity-40"
          >
            {label}
          </button>
        ))}
        <span className="mx-1 h-4 w-px bg-portal-line" aria-hidden />
        <button
          type="button"
          onClick={() => editor.chain().focus().insertContent({ type: 'pageBreak' }).run()}
          className="rounded px-2 py-1 text-portal-micro text-portal-ink hover:bg-portal-bg"
        >
          Page break
        </button>
        <select
          aria-label="Insert signing field"
          value=""
          onChange={(e) => {
            const field = SIGNING_FIELDS.find((f) => f.label === e.target.value);
            if (field) editor.chain().focus().insertContent(field.tag).run();
          }}
          className="rounded border-0 bg-transparent px-1 py-1 text-portal-micro text-portal-ink hover:bg-portal-bg"
        >
          <option value="" disabled>
            Insert field…
          </option>
          {SIGNING_FIELDS.map((f) => (
            <option key={f.label} value={f.label}>
              {f.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Insert data field"
          value=""
          onChange={(e) => {
            const item = MERGE_INSERTS.find((m) => m.key === e.target.value);
            if (item) editor.chain().focus().insertContent(item.html).run();
          }}
          className="rounded border-0 bg-transparent px-1 py-1 text-portal-micro text-portal-ink hover:bg-portal-bg"
        >
          <option value="" disabled>
            Insert data…
          </option>
          {MERGE_INSERTS.map((m) => (
            <option key={m.key} value={m.key}>
              {m.label}
            </option>
          ))}
        </select>
      </div>
      {library.data ? <style>{scopeDocumentCss(library.data.stylesheet)}</style> : null}
      <div className="mx-auto max-h-[72vh] w-[816px] max-w-full overflow-y-auto bg-white px-14 py-12">
        <div className="contract-doc-surface">
          {library.data ? (
            // The page's brand row (logo + address) — shell furniture shown
            // for WYSIWYG parity, not part of the editable body.
            // eslint-disable-next-line react/no-danger -- server-owned constant markup
            <div contentEditable={false} dangerouslySetInnerHTML={{ __html: library.data.brandHtml }} />
          ) : null}
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  );
}
